// Core Web Vitals collector. Delegates to the web-vitals library for the
// hard parts (LCP attribution, INP processing, CLS session windowing) — we
// re-export each callback as an event_type='web_vital' beacon event.
//
// Why we import from 'web-vitals/attribution': it gives us per-metric debug
// fields (LCP element selector, INP interaction target, CLS shifted nodes)
// that turn raw numbers into actionable defects. The cost is ~2KB gzipped;
// for a RUM collector that's a fair trade.

import { onLCP, onINP, onCLS, onFCP, onTTFB } from 'web-vitals/attribution';
import { EVENT_TYPES } from '../schema.js';
import { safe, now } from '../safe.js';
import { shouldSample } from '../config.js';

// All five Core Web Vitals are 100% sampled per the spec. We expose the
// option to downsample via config in case a host wants to.
const VITALS = [
  ['LCP', onLCP],
  ['INP', onINP],
  ['CLS', onCLS],
  ['FCP', onFCP],
  ['TTFB', onTTFB]
];

export function initVitals(viewIdProvider, emit) {
  for (const [name, register] of VITALS) {
    try {
      register(safe((metric) => {
        if (!shouldSample(EVENT_TYPES.WEB_VITAL)) return;
        emit(toEvent(name, metric, viewIdProvider()));
      }));
    } catch (_) { /* silent — never break host */ }
  }
}

function toEvent(name, metric, viewId) {
  const e = {
    id: viewId + ':' + metric.id,
    t: now(),
    tp: EVENT_TYPES.WEB_VITAL,
    n: name,
    mv: metric.value,
    mu: name === 'CLS' ? 'score' : 'ms',
    mr: metric.rating || '',
    mnt: metric.navigationType || '',
    dim: {},
    met: { delta: metric.delta }
  };
  packAttribution(name, metric.attribution, e);
  return e;
}

// web-vitals attribution shapes change between major versions; the
// scripts/verify-web-vitals-api.mjs check guards the surface we import,
// while this function isolates the breakage radius to one place.
function packAttribution(name, a, e) {
  if (!a) return;
  const dim = e.dim;
  const met = e.met;
  switch (name) {
    case 'LCP':
      if (a.element)       dim.lcp_element = String(a.element).slice(0, 256);
      if (a.url)           dim.lcp_url = a.url;
      if (a.loadState)     dim.lcp_load_state = a.loadState;
      if (typeof a.timeToFirstByte === 'number')     met.lcp_ttfb = a.timeToFirstByte;
      if (typeof a.resourceLoadDelay === 'number')   met.lcp_resource_delay = a.resourceLoadDelay;
      if (typeof a.resourceLoadDuration === 'number')met.lcp_resource_duration = a.resourceLoadDuration;
      if (typeof a.elementRenderDelay === 'number')  met.lcp_render_delay = a.elementRenderDelay;
      break;
    case 'INP':
      if (a.interactionTarget) dim.inp_target = String(a.interactionTarget).slice(0, 256);
      if (a.interactionType)   dim.inp_type = a.interactionType;
      if (a.loadState)         dim.inp_load_state = a.loadState;
      if (typeof a.inputDelay === 'number')          met.inp_input_delay = a.inputDelay;
      if (typeof a.processingDuration === 'number')  met.inp_processing = a.processingDuration;
      if (typeof a.presentationDelay === 'number')   met.inp_presentation = a.presentationDelay;
      break;
    case 'CLS':
      if (a.largestShiftTarget) dim.cls_target = String(a.largestShiftTarget).slice(0, 256);
      if (a.loadState)          dim.cls_load_state = a.loadState;
      if (typeof a.largestShiftValue === 'number') met.cls_largest_shift = a.largestShiftValue;
      if (typeof a.largestShiftTime === 'number')  met.cls_largest_shift_time = a.largestShiftTime;
      break;
    case 'FCP':
      if (a.loadState) dim.fcp_load_state = a.loadState;
      if (typeof a.timeToFirstByte === 'number') met.fcp_ttfb = a.timeToFirstByte;
      if (typeof a.firstByteToFCP === 'number')  met.fcp_from_ttfb = a.firstByteToFCP;
      break;
    case 'TTFB':
      if (typeof a.waitingDuration === 'number')    met.ttfb_waiting = a.waitingDuration;
      if (typeof a.cacheDuration === 'number')      met.ttfb_cache = a.cacheDuration;
      if (typeof a.dnsDuration === 'number')        met.ttfb_dns = a.dnsDuration;
      if (typeof a.connectionDuration === 'number') met.ttfb_conn = a.connectionDuration;
      if (typeof a.requestDuration === 'number')    met.ttfb_request = a.requestDuration;
      break;
  }
}
